import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Loader2, Shield, Users } from "lucide-react";

const UNITS = [
  { key: "GK", label: "Goalkeeper" },
  { key: "DEF", label: "Defence" },
  { key: "MID", label: "Midfield" },
  { key: "FWD", label: "Attack" },
];

const readyColor = (r) => (r >= 80 ? "#33d17a" : r >= 60 ? "#00e5ff" : r >= 45 ? "#ffb800" : "#ff3b30");

const Side = ({ side, align }) => {
  if (!side) return null;
  const grouped = UNITS.map((u) => ({ ...u, players: (side.starting_xi || []).filter((p) => p.position === u.key) }))
    .filter((u) => u.players.length);
  return (
    <div className="flex-1 min-w-[260px]" data-testid={`lineup-side-${align}`}
      data-octon-section={`${side.club} ${align} team lineup xi bench`} data-octon-label={`${side.club} line-up`}>
      <div className={`mb-3 flex items-center gap-2.5 ${align === "away" ? "sm:flex-row-reverse sm:text-right" : ""}`}>
        {side.logo && <img src={side.logo} alt="" className="h-8 w-8 object-contain" />}
        <div className={align === "away" ? "sm:items-end sm:flex sm:flex-col" : ""}>
          <p className="font-head text-lg font-bold leading-none tracking-tight text-white" data-testid={`lineup-club-${align}`}>{side.club}</p>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-tac text-[10px] tracking-widest txt-cyan" data-testid={`lineup-formation-${align}`}>{side.formation}</span>
            {side.shape_belief_pct != null && (
              <span className="font-mono text-[11px]" style={{ color: "#ccff00" }} title="OCTON's confidence in this shape for the club — not the match win probability">{side.shape_belief_pct}% shape belief</span>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {grouped.map((u) => (
          <div key={u.key}>
            <p className="mb-1 font-tac text-[8px] tracking-widest text-white/35">{u.label}</p>
            <div className="space-y-1">
              {u.players.map((p) => (
                <div key={p.name} className="flex items-center justify-between rounded-lg border border-white/8 bg-white/[0.02] px-2.5 py-1.5" data-testid={`lineup-player-${align}-${(p.id ?? p.name)}`}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-[13px] text-white/85">{p.name}</span>
                    {p.role && <span className="shrink-0 rounded border border-white/12 px-1 font-tac text-[8px] tracking-wider text-white/55" title={p.role_label || ""}>{p.role}</span>}
                    {p.lineup_only && <span className="shrink-0 font-tac text-[7px] tracking-wider text-amber-300/70" title="From real team sheets — not in the provider's squad list">SHEET</span>}
                    {p.note && <span className="shrink-0 font-tac text-[7px] tracking-wider text-cyan-300/70" title={p.note}>UEFA-BAN ONLY</span>}
                  </span>
                  <span className="ml-2 flex shrink-0 items-center gap-2">
                    {p.last_xi && <span className="font-tac text-[7px] tracking-wider txt-cyan" title="Started the last match">LAST XI</span>}
                    {p.recent_starts > 0 && (
                      <span className="font-tac text-[8px] tracking-wider text-white/35">{p.recent_starts}/5</span>
                    )}
                    <span className="h-1.5 w-1.5 rounded-full" style={{ background: readyColor(p.readiness) }} title={`${p.readiness}% ready`} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {side.usual_subs?.came_on?.length > 0 && (
        <div className="mt-3 border-t border-white/6 pt-3" data-testid={`lineup-usual-subs-${align}`}
          data-octon-section={`${side.club} usual substitutes who comes on off bench impact subs`} data-octon-label={`${side.club} usual subs`} data-octon-state="ready">
          <p className="mb-1.5 font-tac text-[8px] tracking-widest text-white/35">USUAL SUBS · REAL EVENTS, LAST {side.usual_subs.matches} MATCHES</p>
          <div className="flex flex-wrap gap-1.5">
            {side.usual_subs.came_on.slice(0, 6).map((s) => (
              <span key={s.name} className="rounded-full border border-cyan-400/20 bg-cyan-400/5 px-2.5 py-1 text-[11px] text-white/70" title={`came on ${s.times}× · typical minute ${s.avg_minute}'`}>
                {s.name} <span className="text-white/35">· on {s.times}× ~{s.avg_minute}'</span>
              </span>
            ))}
          </div>
          {side.usual_subs.came_off?.length > 0 && (
            <p className="mt-1.5 text-[10px] text-white/40">usually comes off: {side.usual_subs.came_off.slice(0, 4).map((s) => `${s.name} (~${s.avg_minute}')`).join(", ")}</p>
          )}
        </div>
      )}

      {side.bench?.length > 0 && (
        <div className="mt-4 border-t border-white/6 pt-3" data-testid={`lineup-bench-${align}`}>
          <p className="mb-1.5 flex items-center gap-1.5 font-tac text-[8px] tracking-widest text-white/35">
            <Users size={11} /> BENCH
          </p>
          <div className="flex flex-wrap gap-1.5">
            {side.bench.map((b) => (
              <span key={b.name} title={b.reason || ""}
                className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[11px] text-white/60">
                {b.name} <span className="text-white/30">· {b.pos}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const MatchupLineups = ({ teamId, oppId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { const h = () => setTick((t) => t + 1); window.addEventListener("octon:team-news-changed", h); return () => window.removeEventListener("octon:team-news-changed", h); }, []);

  useEffect(() => {
    if (!teamId || !oppId || Number(teamId) === Number(oppId)) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setErr(null);
    api.get(`/matchup/lineups?team_id=${Number(teamId)}&opponent_id=${Number(oppId)}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr("Could not load line-ups"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [teamId, oppId, tick]);

  if (!teamId || !oppId || Number(teamId) === Number(oppId)) return null;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="card-tac mt-8 p-5 sm:p-6" data-testid="matchup-lineups"
      data-octon-section="lineups line-ups both teams team sheets starting eleven bench squads"
      data-octon-label="Probable Line-ups — Both Teams"
      data-octon-state={loading ? "loading" : err ? "error" : data ? "ready" : "loading"}>
      <div className="mb-4 flex items-center gap-2">
        <Shield size={15} className="txt-cyan" />
        <span className="font-tac text-[10px] tracking-[0.3em] text-white/50">PROBABLE LINE-UPS · BOTH TEAMS</span>
      </div>

      {loading && (
        <div className="flex items-center gap-2 py-8 text-sm text-white/50" data-testid="lineups-loading">
          <Loader2 size={16} className="animate-spin" /> Building both team-sheets…
        </div>
      )}
      {err && !loading && <p className="py-6 text-sm text-red-300" data-testid="lineups-error">{err}</p>}

      {data && !loading && (
        <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
          <Side side={data.home} align="home" />
          <div className="hidden w-px shrink-0 bg-white/8 sm:block" />
          <Side side={data.away} align="away" />
        </div>
      )}
    </motion.div>
  );
};
